import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { ExpandMore, ExpandLess } from '@material-ui/icons';
import { lightTheme as theme } from '../../theme';

const KanbanGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 24px;
    align-items: start;
`;

const ColumnSurface = styled.div`
    background-color: ${theme.colors.gray100};
    border-radius: 12px;
    padding: 12px;
    min-height: 200px;
    border: 1px solid ${theme.colors.border};
`;

const ColumnHeaderStyled = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px 16px;
    border-bottom: 2px solid ${({ color }) => color || 'transparent'};
    margin-bottom: 12px;
    cursor: pointer;

    h3 {
        margin: 0;
        font-size: 16px;
        font-weight: 700;
        color: ${theme.colors.text};
        display: flex;
        align-items: center;
        gap: 8px;
    }
`;

const CountBadge = styled.span`
    background: ${theme.colors.gray200};
    color: ${theme.colors.text};
    font-size: 12px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 99px;
    min-width: 24px;
    text-align: center;
`;

const LoadMoreButton = styled.button`
    height: 40px;
    border-radius: 999px;
    border: 1px solid ${theme.colors.border};
    background-color: ${theme.colors.paper};
    color: ${theme.colors.text};
    padding: 0 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s;
    width: 100%;
    margin-top: 8px;

    &:hover {
        background-color: ${theme.colors.gray100};
        border-color: ${theme.colors.gray300};
    }
`;

const CardsStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const KanbanColumn = ({
    columnKey,
    group,
    isExpanded,
    showAll,
    onToggleExpand,
    onToggleShowAll,
    renderCard,
    emptyMessage
}) => {
    const { items, title, color } = group;
    const initialItemsToShow = 5;
    const displayedItems = showAll ? items : items.slice(0, initialItemsToShow);
    const hasMoreItems = items.length > initialItemsToShow;

    return (
        <ColumnSurface>
            <ColumnHeaderStyled
                color={color}
                onClick={() => onToggleExpand(columnKey)}
            >
                <h3>
                    {title}
                    <CountBadge>{items.length}</CountBadge>
                </h3>
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </ColumnHeaderStyled>

            {isExpanded && (
                <CardsStack>
                    {items.length === 0 ? (
                        <p style={{ textAlign: 'center', color: theme.colors.textMuted, fontStyle: 'italic', padding: 20 }}>
                            {emptyMessage || 'Nenhum item nesta coluna'}
                        </p>
                    ) : (
                        <>
                            {displayedItems.map((item) => renderCard(item))}

                            {hasMoreItems && (
                                <LoadMoreButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onToggleShowAll(columnKey);
                                    }}
                                >
                                    {showAll ?
                                        'Mostrar menos' :
                                        `Ver mais (${items.length - initialItemsToShow})`
                                    }
                                </LoadMoreButton>
                            )}
                        </>
                    )}
                </CardsStack>
            )}
        </ColumnSurface>
    );
};

KanbanColumn.propTypes = {
    columnKey: PropTypes.string.isRequired,
    group: PropTypes.shape({
        items: PropTypes.array.isRequired,
        title: PropTypes.string.isRequired,
        color: PropTypes.string,
        showAll: PropTypes.bool
    }).isRequired,
    isExpanded: PropTypes.bool.isRequired,
    showAll: PropTypes.bool,
    onToggleExpand: PropTypes.func.isRequired,
    onToggleShowAll: PropTypes.func.isRequired,
    renderCard: PropTypes.func.isRequired,
    emptyMessage: PropTypes.string
};

export default function KanbanBoard({
    groups,
    columnStates,
    onToggleExpand,
    onToggleShowAll,
    renderCard,
    emptyMessage
}) {
    // Sort groups by order property
    const sortedKeys = Object.keys(groups).sort((a, b) => groups[a].order - groups[b].order);

    return (
        <KanbanGrid>
            {sortedKeys.map((key) => (
                <KanbanColumn
                    key={key}
                    columnKey={key}
                    group={groups[key]}
                    isExpanded={columnStates[key]?.expanded !== false}
                    showAll={groups[key].showAll}
                    onToggleExpand={onToggleExpand}
                    onToggleShowAll={onToggleShowAll}
                    renderCard={renderCard}
                    emptyMessage={emptyMessage}
                />
            ))}
        </KanbanGrid>
    );
}

KanbanBoard.propTypes = {
    groups: PropTypes.object.isRequired,
    columnStates: PropTypes.object.isRequired,
    onToggleExpand: PropTypes.func.isRequired,
    onToggleShowAll: PropTypes.func.isRequired,
    renderCard: PropTypes.func.isRequired,
    emptyMessage: PropTypes.string
};
